import { Icon24UtilityChevron } from '@care/react-icons';
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Typography,
  Box,
  Theme,
  Container,
} from '@mui/material';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import useMediaQuery from '@mui/material/useMediaQuery';
import { seoSegmentationListingPage_getSEOSegmentationLinkSections_linkList as CostGuideLinks } from '@/__generated__/seoSegmentationListingPage';
import {
  costGuide_costGuide_CostGuideSuccess_costGuide_location as CostGuideLocationType,
  costGuide_costGuide_CostGuideSuccess_costGuide_location_state as CostGuideStateType,
} from '@/__generated__/costGuide';
import { getSEOSegmentationLinkSections_getSEOSegmentationLinkSections as CostGuideLinkSections } from '@/__generated__/getSEOSegmentationLinkSections';
import { InternalLinkSectionId } from '@/__generated__/globalTypes';

interface CostGuideLinkListProps {
  costGuideLinkList: CostGuideLinkSections[];
  city: CostGuideLocationType['city'] | null;
  stateCode: CostGuideStateType['stateCode'] | null;
}

const CostGuideLinkList = ({ costGuideLinkList, city, stateCode }: CostGuideLinkListProps) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const seoLinks = costGuideLinkList.filter((costGuideSeoLink) => {
    return costGuideSeoLink.linkList.length > 0;
  });

  const links = (linkList: CostGuideLinks[]) => {
    return linkList.map((link: CostGuideLinks, index: number) => (
      <Link
        variant="link2"
        href={link.url}
        key={`${link.content}-${index + 1}`}
        sx={{
          cursor: 'pointer',
          lineHeight: '30px',
          ':hover': {
            textDecoration: 'none',
          },
        }}>
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(he.decode(link.content)),
          }}
        />
      </Link>
    ));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 5, md: 8 }, mb: { xs: 4.5, md: 8 } }}>
      <Grid item xs={12}>
        {seoLinks.length > 0 && (
          <Box>
            {seoLinks.map((costGuideSeoLink: CostGuideLinkSections) => (
              <Box key={costGuideSeoLink.title} display="flex" flexDirection="column" width="100%">
                <Accordion defaultExpanded={!isMobileOrSmaller} elevation={0}>
                  <AccordionSummary
                    sx={(theme: Theme) => ({
                      width: 'fit-content',
                      padding: 0,
                      '& .MuiButtonBase-root': {
                        marginTop: theme.spacing(1),
                      },
                    })}
                    expandIcon={<Icon24UtilityChevron color="text.secondary" />}>
                    <Typography variant="h4" color="text.secondary">
                      {costGuideSeoLink.id === InternalLinkSectionId.RELATED_SERVICES
                        ? `${costGuideSeoLink.title} in ${city}, ${stateCode}`
                        : costGuideSeoLink.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Box display={{ sm: 'flex' }}>
                      <Box display="flex" flexDirection="column" width="100%">
                        {links(costGuideSeoLink.linkList).slice(0, 5)}
                      </Box>
                      <Box display="flex" flexDirection="column" width="100%">
                        {links(costGuideSeoLink.linkList).slice(5, 10)}
                      </Box>
                      <Box display="flex" flexDirection="column" width="100%">
                        {links(costGuideSeoLink.linkList).slice(10, 15)}
                      </Box>
                      <Box display="flex" flexDirection="column" width="100%">
                        {links(costGuideSeoLink.linkList).slice(15, 20)}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
          </Box>
        )}
      </Grid>
    </Container>
  );
};

export default CostGuideLinkList;
